import { LoadingIcon } from '@assets/icons';
import { TableView } from '@components/button';
import { DeckardFilter } from '@components/filter';
import {
  InfiniteScrollGridComponents,
  InfiniteScrollTableComponents,
  useGraphqlPagination,
} from '@components/infiniteScroll';
import { Typography } from '@components/typography';
import {
  GetWorkspacesQuery,
  Organization,
  Workspace,
  WorkspaceFilter,
  useGetWorkspacesQuery,
  useGetWorkspacesSearchDetailQuery,
} from '@generated/graphql';
import { Box, Grid, useTheme } from '@mui/material';
import { LandingPageWorkspaceCard, OrganizationWorkspacesHeader } from '@subsets/landing-page';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableVirtuoso, VirtuosoGrid } from 'react-virtuoso';
import { OrganizationJobQueue } from './OrganizationJobQueue';
import { WorkspaceTableHeader } from './WorkspaceTableHeader';
import { WorkspaceTableRow } from './WorkspaceTableRow';

type OrganizationWorkspacesProps = {
  organization?: Organization;
};

export const OrganizationWorkspaces = ({ organization }: OrganizationWorkspacesProps) => {
  const { palette } = useTheme();

  const [filter, setFilter] = useState<DeckardFilter<WorkspaceFilter>>({
    channels: { type: 'exact', value: [], display: [] },
    dates: { type: 'date', value: [], display: [] },
    tags: { type: 'string', value: [], display: [] },
    texts: { type: 'string', value: [], display: [] },
    users: { type: 'exact', value: [], display: [] },
  });

  const queryFilter = useMemo(() => {
    return Object.entries(filter)
      .filter(([, value]) => value.value.length > 0)
      .reduce((acc, [key, value]) => {
        acc[key] = value.value;
        return acc;
      }, {} as WorkspaceFilter);
  }, [filter]);
  const { data, refetch: refetchSearchDetail } = useGetWorkspacesSearchDetailQuery({
    variables: {
      organizationId: organization?.organizationId,
    },
  });
  const searchDetail = data?.getWorkspacesSearchDetail;

  const [tableView, setTableView] = useState<TableView>('grid');
  const {
    data: workspaces,
    hasMore,
    loading: workspacesLoading,
    fetchMore: fetchMoreWorkspaces,
    refetch: refetchWorkspaces,
  } = useGraphqlPagination<GetWorkspacesQuery, Workspace>(
    useGetWorkspacesQuery({
      variables: {
        organizationId: organization?.organizationId,
        filters: queryFilter,
        limit: 30,
      },
      fetchPolicy: 'cache-first',
    }),
    'getWorkspaces',
    'workspaceId',
  );

  useEffect(() => {
    if (workspaces.length < 30) {
      void fetchMoreWorkspaces();
    }
  }, [workspaces, fetchMoreWorkspaces]);

  const refetch = () => {
    refetchWorkspaces();
    void refetchSearchDetail();
  };

  const tableRow = (_index, workspace) => <WorkspaceTableRow {...workspace} />;

  const item = useCallback((_, workspace: Workspace) => {
    return <LandingPageWorkspaceCard key={workspace?.workspaceId} expired={false} {...workspace} />;
  }, []);

  return (
    <Grid container height="100%">
      <Grid
        item
        xs={9}
        pt={5}
        pr={5}
        sx={{ height: '100%', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <OrganizationWorkspacesHeader
          tableView={tableView}
          filter={filter}
          organizationId={organization?.organizationId}
          organizationName={organization?.name}
          searchDetail={searchDetail}
          refetch={refetch}
          setFilter={setFilter}
          setTableView={setTableView}
        />
        {workspaces?.length > 0 ? (
          tableView === 'grid' ? (
            <VirtuosoGrid
              totalCount={workspaces?.length}
              endReached={fetchMoreWorkspaces}
              components={InfiniteScrollGridComponents}
              data={[...workspaces, ...(hasMore ? [{ workspaceId: 'loading' }] : [])]}
              itemContent={item}
            />
          ) : (
            <Box
              flex={1}
              data-test-id="virtuoso-item-list"
              sx={{ backgroundColor: palette.background.paper, p: 6 }}
            >
              <TableVirtuoso
                data={workspaces}
                endReached={fetchMoreWorkspaces}
                components={InfiniteScrollTableComponents}
                fixedHeaderContent={WorkspaceTableHeader}
                itemContent={tableRow}
                increaseViewportBy={400}
              />
            </Box>
          )
        ) : (
          <Box display="flex" height="90%" alignItems="center" justifyContent="center">
            {workspacesLoading ? (
              <LoadingIcon size={16} />
            ) : workspaces.length === 0 ? (
              <Typography>Your organization has no workspaces.</Typography>
            ) : (
              <Typography>No workspaces found that match your search.</Typography>
            )}
          </Box>
        )}
      </Grid>
      <OrganizationJobQueue organizationId={organization?.organizationId} />
    </Grid>
  );
};
